import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  dbLoadEntries, dbInsertEntry, dbUpdateEntry, dbDeleteEntry, dbBatchUpdateEntries, dbPinEntry,
  dbSetTdtExpedite, dbCancelTdtExpedite,
} from "../db";
import { calcSection, toMins, getAirlineCode } from "../utils/timeUtils";
import { learnRoutes } from "../utils/routeCache";
import { playEntryChime } from "../utils/audioUtils";
import { hapticMedium, hapticLight, hapticWarning } from "../utils/haptics";
import { LHG_CARRIERS } from "../components/shared";
import { findSiblingUpdates, buildImportPatch, appendRemark, detectChanges } from "../utils/entryUtils";
import { isOpenStatus, completionTab } from "../utils/constants";

const CHANGE_TTL_MS = 30 * 60 * 1000; // how long a gate/time change stays flagged on a card

const STATUS_ORDER = ["PROTECTED","CONFIRMED","UNABLE TO CONFIRM","OFFLOADED","COMPLETED",""];

export function useEntries({ appDate, parsedShift, isUA, myAirline, activeSCCO, sessionReady }) {
  const [entries,          setEntries]          = useState([]);
  // Gate/time changes the sync made since the last poll, shown on cards for a while:
  // Map<id, [{ kind, label, at }]>
  const [recentChanges,    setRecentChanges]    = useState(new Map());
  const entriesRef = useRef(entries);
  useEffect(() => { entriesRef.current = entries; }, [entries]);
  const [newEntryIds,      setNewEntryIds]      = useState(new Set());
  const [loading,          setLoading]          = useState(false);
  const [editEntry,        setEditEntry]        = useState(null);
  const [viewEntry,        setViewEntry]        = useState(null);
  const [importedEntryIds, setImportedEntryIds] = useState(new Set());
  const [activeTab,        setActiveTab]        = useState("active");
  const [filterCompletion, setFilterCompletion] = useState("open");
  const [filterStatus,     setFilterStatus]     = useState("ALL");
  const [filterInAirline,  setFilterInAirline]  = useState("ALL");
  const [filterOutAirline, setFilterOutAirline] = useState("ALL");
  const [filterOrigin,     setFilterOrigin]     = useState("ALL");
  const [filterDest,       setFilterDest]       = useState("ALL");
  const [filterOverdue,    setFilterOverdue]    = useState(false);
  const [filterHideP2P,   setFilterHideP2P]   = useState(false);
  const [filterTimeFrom,   setFilterTimeFrom]   = useState("");
  const [filterTimeTo,     setFilterTimeTo]     = useState("");
  const [searchQuery,      setSearchQuery]      = useState("");
  const [showFilterModal,  setShowFilterModal]  = useState(false);
  const [sortBy,           setSortBy]           = useState("std");
  const [sortDir,          setSortDir]          = useState("asc");

  const prevEntryIds  = useRef(null);
  const pendingWrite  = useRef(false);
  const isUARef       = useRef(isUA);
  const deletedKeys   = useRef(new Set()); // inFlt|outFlt keys deleted this session

  const entryMatchKey = (e) => `${(e.inFlt || "").toUpperCase()}|${(e.outFlt || "").toUpperCase()}`;

  // Keep isUARef in sync (polled by interval, avoids stale closure)
  useEffect(() => { isUARef.current = isUA; }, [isUA]);

  // Load entries from Supabase when date changes or session becomes ready
  useEffect(() => {
    if (!sessionReady) return;
    setLoading(true);
    // Timeout safety — if network is blocked, stop spinning after 3s
    const timeout = setTimeout(() => setLoading(false), 3000);
    dbLoadEntries(appDate).then(rows => {
      clearTimeout(timeout);
      prevEntryIds.current = new Set(rows.map(r => r.id));
      setEntries(rows);
      setLoading(false);
    }).catch(() => {
      clearTimeout(timeout);
      setLoading(false);
    });
  }, [appDate, sessionReady]);

  // Poll for new entries every 15 seconds
  useEffect(() => {
    if (!sessionReady) return;
    const interval = setInterval(() => {
      if (!pendingWrite.current) {
        dbLoadEntries(appDate).then(rows => {
          const filtered = rows.filter(r => !deletedKeys.current.has(entryMatchKey(r)));
          // Delete any re-inserted rows that the user already deleted this session
          rows.filter(r => deletedKeys.current.has(entryMatchKey(r))).forEach(r => dbDeleteEntry(r.id));
          if (prevEntryIds.current !== null) {
            const fresh = filtered.filter(r => !prevEntryIds.current.has(r.id));
            if (fresh.length > 0) {
              playEntryChime();
              const freshIds = new Set(fresh.map(r => r.id));
              setNewEntryIds(prev => new Set([...prev, ...freshIds]));
            }
          }
          prevEntryIds.current = new Set(filtered.map(r => r.id));
          const found = detectChanges(entriesRef.current, filtered);
          setRecentChanges(prev => {
            const cutoff = Date.now() - CHANGE_TTL_MS;
            const next = new Map();
            for (const [id, list] of prev) {
              const kept = list.filter(c => c.at > cutoff);
              if (kept.length) next.set(id, kept);
            }
            for (const [id, list] of found) {
              // A newer change of the same kind replaces the older one
              const kinds = new Set(list.map(c => c.kind));
              next.set(id, [...(next.get(id) || []).filter(c => !kinds.has(c.kind)),
                            ...list.map(c => ({ ...c, at: Date.now() }))]);
            }
            return next.size === 0 && prev.size === 0 ? prev : next;
          });
          setEntries(filtered);
        }).catch((_err) => {
          // Leave existing entries in place — transient network errors shouldn't
          // clear the board. Auth expiry is handled by onAuthStateChange in useAuth.
        });
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [appDate, sessionReady]);

  // "open" = no status or PROTECTED; "complete" = any other status selected
  const isComplete = (e) => !isOpenStatus(e.status);

  const handleDelete = useCallback((id) => {
    const entry = entries.find(e => e.id === id);
    if (entry) deletedKeys.current.add(entryMatchKey(entry));
    pendingWrite.current = true;
    setEntries(e => e.filter(x => x.id !== id));
    hapticWarning();
    dbDeleteEntry(id).finally(() => {
      setTimeout(() => { pendingWrite.current = false; }, 3000);
    });
  }, [entries]);

  // When saving a new entry, auto-assign section and write to DB
  const handleSave = useCallback((entry) => {
    const withSection = {
      ...entry,
      inAirline: entry.airline || "",
      section: calcSection(entry, parsedShift),
      ...(activeSCCO ? { scco_id: activeSCCO.id, scco_name: activeSCCO.name } : {}),
    };
    deletedKeys.current.delete(entryMatchKey(withSection));
    pendingWrite.current = true;
    setEntries(e => [withSection, ...e]);
    learnRoutes(withSection);
    dbInsertEntry(withSection, appDate).then(() => {
      hapticMedium();
      dbLoadEntries(appDate).then(rows => {
        setEntries(rows);
        pendingWrite.current = false;
      }).catch(() => { pendingWrite.current = false; });
    }).catch(() => { pendingWrite.current = false; });
  }, [activeSCCO, appDate, parsedShift]);

  // Reopen a resolved card — clear status, paxExp, move back to active, append remark
  const handleReopen = useCallback(async (entry) => {
    const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    const tag = "[" + (activeSCCO ? "UA/" + activeSCCO.name : "UA") + " " + time + "] REOPENED";
    const newRemark = entry.remark ? entry.remark + "\n" + tag : tag;
    const reopened = {
      ...entry,
      status: "",
      paxExp: "",
      section: calcSection({ ...entry, status: "" }, parsedShift),
      remark: newRemark,
      ...(activeSCCO ? { scco_id: activeSCCO.id, scco_name: activeSCCO.name } : {}),
      // A leftover TDT suggestion would otherwise drop the card into Pending, not Open
      ...(entry.pendingStatus ? { pendingStatus: "", pendingDismissed: entry.pendingStatus } : {}),
    };
    setEntries(prev => prev.map(e => e.id === reopened.id ? reopened : e));
    await dbUpdateEntry(reopened, appDate, entry.pendingStatus
      ? { pending_status: "", pending_dismissed: entry.pendingStatus } : {});
    setEditEntry(null);
  }, [activeSCCO, appDate, parsedShift]);

  const handleMarkSuccessful = useCallback(async (entry) => {
    const updated = {
      ...entry,
      status: "SUCCESS",
      paxExp: entry.ttlPax || entry.paxExp,
      section: calcSection({ ...entry, status: "SUCCESS" }, parsedShift),
      ...(activeSCCO ? { scco_id: activeSCCO.id, scco_name: activeSCCO.name } : {}),
    };
    setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
    await dbUpdateEntry(updated, appDate);
    hapticLight();
  }, [activeSCCO, appDate, parsedShift]);

  // Update an existing entry in place (or insert if it's a new outbound added during edit)
  const handleEdit = useCallback((entry) => {
    const withSection = {
      ...entry,
      inAirline: entry.airline || entry.inAirline || "",
      section: calcSection(entry, parsedShift),
      ...(activeSCCO && !entry.scco_id ? { scco_id: activeSCCO.id, scco_name: activeSCCO.name } : {}),
    };
    pendingWrite.current = true;
    learnRoutes(withSection);
    // If this id already exists in entries it's an update; otherwise it's a new outbound added during edit
    const exists = entries.some(e => e.id === withSection.id);
    if (exists) {
      const updatedSiblings = findSiblingUpdates(withSection, entries);
      setEntries(prev => prev.map(e => {
        if (e.id === withSection.id) return withSection;
        const sibling = updatedSiblings.find(s => s.id === e.id);
        return sibling || e;
      }));
      const dbWrites = [
        dbUpdateEntry(withSection, appDate),
        ...updatedSiblings.map(sib => dbUpdateEntry(sib, appDate)),
      ];
      Promise.all(dbWrites).then(() => {
        hapticLight();
        dbLoadEntries(appDate).then(rows => {
          setEntries(rows);
          pendingWrite.current = false;
        }).catch(() => { pendingWrite.current = false; });
      }).catch(() => { pendingWrite.current = false; });
    } else {
      setEntries(e => [withSection, ...e]);
      dbInsertEntry(withSection, appDate).then(() => {
        hapticMedium();
        dbLoadEntries(appDate).then(rows => {
          setEntries(rows);
          pendingWrite.current = false;
        }).catch(() => { pendingWrite.current = false; });
      }).catch(() => { pendingWrite.current = false; });
    }
    setEditEntry(null);
  }, [entries, activeSCCO, appDate, parsedShift]);

  // Import Excel — delete existing auto-imported entries, insert new ones
  const handleImport = useCallback(async (importedEntries) => {
    pendingWrite.current = true;
    // Match key: inFlt + outFlt (case-insensitive)
    const matchKey = e => `${(e.inFlt || "").toUpperCase()}|${(e.outFlt || "").toUpperCase()}`;

    // Detect thru-flight duplicates already in DB (same match key, different dest).
    // Keep the first entry per key; delete the extras so they stop multiplying on every import.
    const existingByKey = new Map();
    const thruDuplicateIds = [];
    for (const e of entries) {
      const key = matchKey(e);
      if (existingByKey.has(key)) {
        thruDuplicateIds.push(e.id);
      } else {
        existingByKey.set(key, e);
      }
    }
    if (thruDuplicateIds.length > 0) {
      await Promise.all(thruDuplicateIds.map(id => dbDeleteEntry(id)));
    }

    const toInsert = [];
    const toUpdate = []; // { id, dbPatch }
    for (const imported of importedEntries) {
      const key      = matchKey(imported);
      const existing = existingByKey.get(key);
      if (!existing) {
        if (deletedKeys.current.has(key)) continue; // user deleted this — don't re-import
        toInsert.push(imported);
      } else {
        const dbPatch = buildImportPatch(imported, existing);
        if (dbPatch) toUpdate.push({ id: existing.id, dbPatch });
        existingByKey.delete(key);
      }
    }
    // Delete auto-imported entries no longer present in the new sheet,
    // but only if they fall within the current shift window AND haven't been worked on.
    // Entries from a previous SCCO's shift (arrival before our shift start) or entries
    // that already have a status/ATA/remark are preserved.
    const stale = [...existingByKey.values()].filter(e => {
      if (!e.autoImported) return false;
      // Never delete entries that have been worked on
      if (e.status || e.monitoring || e.pendingStatus || e.ata || e.atd || e.remark) return false;
      // Never delete entries that arrived before the current shift started
      if (parsedShift) {
        const arrMins = toMins(e.sta || e.eta);
        if (arrMins !== null && arrMins < parsedShift.start) return false;
      }
      return true;
    });
    await Promise.all(stale.map(e => dbDeleteEntry(e.id)));
    await Promise.all(toInsert.map(e =>
      dbInsertEntry(e, appDate, e.pendingStatus ? { pending_status: e.pendingStatus } : {})));
    await dbBatchUpdateEntries(toUpdate);

    const prevIds = new Set(entries.map(e => e.id));
    const rows = await dbLoadEntries(appDate);
    // Re-delete any rows that were re-inserted by TDT sync since the user deleted them
    rows.filter(r => deletedKeys.current.has(entryMatchKey(r))).forEach(r => dbDeleteEntry(r.id));
    const filtered = rows.filter(r => !deletedKeys.current.has(entryMatchKey(r)));
    const newIds = new Set(filtered.filter(r => !prevIds.has(r.id)).map(r => r.id));
    prevEntryIds.current = new Set(filtered.map(r => r.id));
    setEntries(filtered);
    pendingWrite.current = false;

    // Green glow on newly inserted cards for 7 seconds
    if (newIds.size > 0) {
      setImportedEntryIds(newIds);
      setTimeout(() => setImportedEntryIds(new Set()), 7000);
    }

    return { newCount: toInsert.length, updatedCount: toUpdate.length };
  }, [entries, appDate, parsedShift]);

  const handlePin = useCallback((id, pinned) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, pinned } : e));
    dbPinEntry(id, pinned).catch(() => {
      // revert on failure
      setEntries(prev => prev.map(e => e.id === id ? { ...e, pinned: !pinned } : e));
    });
  }, []);

  // Pending tab: accept the TDT-detected outcome as the card's status → Complete.
  // Pax expedited comes from TDT's success count (filled by the sync); SUCCESS falls back
  // to total pax, same as Mark Successful.
  const handleApprovePending = useCallback(async (entry) => {
    const status = entry.pendingStatus;
    if (!status) return;
    const approved = {
      ...entry,
      status,
      pendingStatus: "",
      ...(status === "SUCCESS" ? { paxExp: entry.paxExp || entry.ttlPax } : {}),
      section: calcSection({ ...entry, status }, parsedShift),
      remark: appendRemark(entry.remark, `Approved TDT result: ${status}`, "UA", activeSCCO?.name),
      ...(activeSCCO ? { scco_id: activeSCCO.id, scco_name: activeSCCO.name } : {}),
    };
    setEntries(prev => prev.map(e => e.id === approved.id ? approved : e));
    hapticLight();
    await dbUpdateEntry(approved, appDate, { pending_status: "" });
  }, [activeSCCO, appDate, parsedShift]);

  // Pending tab: reject → back to Open. pending_dismissed stops the sync re-suggesting
  // the same outcome; it only suggests again if TDT's outcome changes.
  const handleRejectPending = useCallback(async (entry) => {
    const status = entry.pendingStatus;
    if (!status) return;
    const rejected = {
      ...entry,
      pendingStatus: "",
      pendingDismissed: status,
      remark: appendRemark(entry.remark, `Rejected TDT result: ${status}`, "UA", activeSCCO?.name),
    };
    setEntries(prev => prev.map(e => e.id === rejected.id ? rejected : e));
    hapticLight();
    await dbUpdateEntry(rejected, appDate, { pending_status: "", pending_dismissed: status });
  }, [activeSCCO, appDate]);

  // "Expedite in TDT": queue the card for the TDT add-on, which logs the expedite on its next
  // cycle (all passengers, to the inbound carrier's expeditor) and writes back the result.
  const handleExpediteInTdt = useCallback((entry) => {
    const prev = entry.tdtExpedite;
    const request = { status: "queued", requested_by: activeSCCO?.name || "", requested_at: new Date().toISOString() };
    setEntries(list => list.map(e => e.id === entry.id ? { ...e, tdtExpedite: request } : e));
    hapticLight();
    dbSetTdtExpedite(entry.id, request).catch(() => {
      setEntries(list => list.map(e => e.id === entry.id ? { ...e, tdtExpedite: prev } : e));
    });
  }, [activeSCCO]);

  // Withdraw a request the add-on hasn't picked up yet
  const handleCancelTdtExpedite = useCallback((entry) => {
    const prev = entry.tdtExpedite;
    if (prev?.status !== "queued") return;
    setEntries(list => list.map(e => e.id === entry.id ? { ...e, tdtExpedite: null } : e));
    const restore = (tdtExpedite) => setEntries(list => list.map(e => e.id === entry.id ? { ...e, tdtExpedite } : e));
    dbCancelTdtExpedite(entry.id)
      // Already claimed by the add-on: it's being sent; the next poll shows how it went
      .then(cancelled => { if (!cancelled) restore({ ...prev, status: "sending" }); })
      .catch(() => restore(prev));
  }, []);

  const toggleSort = useCallback((key) => {
    if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("asc"); }
  }, [sortBy]);

  // Partners only see entries involving their airline
  const visibleEntries = useMemo(() => {
    if (isUA) return entries;
    if (!myAirline) return [];
    const al = myAirline.toUpperCase();
    const codes = al === "LHG" ? LHG_CARRIERS : [al];
    return entries.filter(e => codes.some(c =>
      (e.airline    || "").toUpperCase() === c ||
      (e.inAirline  || "").toUpperCase() === c ||
      (e.outAirline || "").toUpperCase() === c ||
      (e.inFlt  || "").toUpperCase().startsWith(c) ||
      (e.outFlt || "").toUpperCase().startsWith(c)
    ));
  }, [entries, isUA, myAirline]);

  // Derive filter options from visible entries
  const filterOptions = useMemo(() => {
    const inAirlines = new Set(), outAirlines = new Set(), origins = new Set(), dests = new Set();
    visibleEntries.forEach(e => {
      const inAl  = getAirlineCode(e.inAirline, e.inFlt);
      const outAl = getAirlineCode(e.outAirline, e.outFlt);
      if (inAl  && inAl.length  === 2) inAirlines.add(inAl);
      if (outAl && outAl.length === 2) outAirlines.add(outAl);
      if (e.origin) origins.add(e.origin.toUpperCase());
      if (e.dest)   dests.add(e.dest.toUpperCase());
    });
    return {
      inAirlines:  [...inAirlines].sort(),
      outAirlines: [...outAirlines].sort(),
      origins:     [...origins].sort(),
      dests:       [...dests].sort(),
    };
  }, [visibleEntries]);

  // Search: flight number, airport or gate ("1169", "ua 1169", "YUL", "77")
  const searchKey = searchQuery.replace(/\s+/g, "").toUpperCase();
  const filtered = useMemo(() => visibleEntries.filter(e => {
    if (searchKey) {
      // While searching, look across all tabs so a flight is found wherever its card is
      const fields = [e.inFlt, e.outFlt, e.origin, e.dest, e.inGate, e.outGate];
      if (!fields.some(v => (v || "").replace(/\s+/g, "").toUpperCase().includes(searchKey))) return false;
    } else if (completionTab(e) !== filterCompletion) return false; // Completion tab
    // Status
    if (filterStatus !== "ALL" && e.status !== filterStatus) return false;
    // Inbound airline
    if (filterInAirline !== "ALL" && getAirlineCode(e.inAirline, e.inFlt) !== filterInAirline) return false;
    // Outbound airline
    if (filterOutAirline !== "ALL" && getAirlineCode(e.outAirline, e.outFlt) !== filterOutAirline) return false;
    // Origin / destination
    if (filterOrigin !== "ALL" && (e.origin || "").toUpperCase() !== filterOrigin) return false;
    if (filterDest   !== "ALL" && (e.dest   || "").toUpperCase() !== filterDest)   return false;
    // Overdue (no status, not departed, past scheduled departure, today only)
    if (filterOverdue) {
      if (e.status) return false;
      if (e.atd && e.atd !== "XCL") return false;
      const depStr = e.etd || e.std;
      if (!depStr) return false;
      const [dh, dm] = depStr.split(":").map(Number);
      if (isNaN(dh)) return false;
      if (appDate !== new Date().toISOString().slice(0, 10)) return false;
      const now = new Date();
      if (now.getHours() * 60 + now.getMinutes() <= dh * 60 + dm) return false;
    }
    // Hide point-to-point (no UA leg)
    if (filterHideP2P) {
      const inAl  = getAirlineCode(e.inAirline, e.inFlt);
      const outAl = getAirlineCode(e.outAirline, e.outFlt);
      if (inAl !== "UA" && outAl !== "UA") return false;
    }
    // Time window
    if (filterTimeFrom || filterTimeTo) {
      const depStr = e.etd || e.std;
      if (!depStr) return false;
      const [dh, dm] = depStr.split(":").map(Number);
      if (isNaN(dh)) return false;
      const depMins = dh * 60 + dm;
      if (filterTimeFrom) {
        const [fh, fm] = filterTimeFrom.split(":").map(Number);
        if (depMins < fh * 60 + fm) return false;
      }
      if (filterTimeTo) {
        const [th, tm] = filterTimeTo.split(":").map(Number);
        if (depMins > th * 60 + tm) return false;
      }
    }
    return true;
  }), [visibleEntries, searchKey, filterCompletion, filterStatus, filterInAirline, filterOutAirline, filterOrigin, filterDest, filterOverdue, filterHideP2P, filterTimeFrom, filterTimeTo, appDate]);

  // Sort helpers — toMins() returns null for missing/invalid times; ?? 9999 sorts them last
  const connTimeVal = (e) => {
    const inTime  = e.ata || e.eta || e.sta;
    const outTime = e.atd || e.etd || e.std;
    if (!inTime || !outTime) return 9999;
    const [ah, am] = inTime.split(":").map(Number);
    const [dh, dm] = outTime.split(":").map(Number);
    if (isNaN(ah) || isNaN(dh)) return 9999;
    let diff = (dh * 60 + dm) - (ah * 60 + am);
    if (diff < 0 && diff + 1440 < 480) diff += 1440;
    return diff;
  };
  const sorted =[...filtered].sort((a, b) => {
    // Pinned entries always float to top
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    let av, bv;
    if      (sortBy === "sta")    { av = toMins(a.ata || a.eta || a.sta) ?? 9999; bv = toMins(b.ata || b.eta || b.sta) ?? 9999; }
    else if (sortBy === "std")    {
      const adjDep = (e) => {
        const dep = toMins(e.atd || e.etd || e.std) ?? 9999;
        const arr = toMins(e.ata || e.eta || e.sta) ?? 9999;
        if (dep !== 9999 && arr !== 9999 && dep < arr && dep + 1440 - arr < 480) return dep + 1440;
        return dep;
      };
      av = adjDep(a); bv = adjDep(b);
    }
    else if (sortBy === "airline"){ av = (a.airline||"").localeCompare(b.airline||""); return sortDir === "asc" ? av : -av; }
    else if (sortBy === "conn")   { av = connTimeVal(a);           bv = connTimeVal(b); }
    else if (sortBy === "status") { av = STATUS_ORDER.indexOf(a.status); bv = STATUS_ORDER.indexOf(b.status); if (av < 0) av = 99; if (bv < 0) bv = 99; }
    else return 0;
    return sortDir === "asc" ? av - bv : bv - av;
  });

  const openCount    = visibleEntries.filter(e => completionTab(e) === "open").length;
  const pendingCount = visibleEntries.filter(e => completionTab(e) === "pending").length;

  return {
    entries, setEntries, loading, newEntryIds,
    clearNewEntryId: (id) => setNewEntryIds(prev => { const next = new Set(prev); next.delete(id); return next; }),
    importedEntryIds, setImportedEntryIds,
    editEntry, setEditEntry, viewEntry, setViewEntry,
    activeTab, setActiveTab,
    filterCompletion, setFilterCompletion,
    filterStatus, setFilterStatus,
    filterInAirline, setFilterInAirline,
    filterOutAirline, setFilterOutAirline,
    filterOrigin, setFilterOrigin,
    filterDest, setFilterDest,
    filterOverdue, setFilterOverdue,
    filterHideP2P, setFilterHideP2P,
    filterTimeFrom, setFilterTimeFrom,
    filterTimeTo, setFilterTimeTo,
    searchQuery, setSearchQuery,
    recentChanges,
    showFilterModal, setShowFilterModal,
    sortBy, setSortBy, sortDir, setSortDir,
    visibleEntries, filterOptions, filtered, sorted,
    isComplete, openCount, pendingCount,
    handleDelete, handleSave, handleReopen, handleEdit, handleMarkSuccessful,
    handleImport, handlePin, handleApprovePending, handleRejectPending, toggleSort,
    handleExpediteInTdt, handleCancelTdtExpedite,
  };
}
